import { pickLocalized, type LocalizedValue, type SupportedLanguage } from '@/features/i18n';

const ERROR_COPY = {
  invalid_credentials: {
    ko: '이메일 또는 비밀번호가 올바르지 않습니다',
    en: 'The email or password is incorrect.',
    ja: 'メールアドレスまたはパスワードが正しくありません。',
  },
  email_already_used: {
    ko: '이미 사용 중인 이메일입니다',
    en: 'This email address is already in use.',
    ja: 'このメールアドレスはすでに使用されています。',
  },
  password_too_short: {
    ko: '비밀번호는 8자 이상이어야 합니다',
    en: 'Use a password with at least 8 characters.',
    ja: 'パスワードは8文字以上で入力してください。',
  },
  invalid_invite: {
    ko: '초대 코드가 이미 사용되었거나 유효하지 않습니다',
    en: 'This invite code has already been used or is invalid.',
    ja: 'この招待コードは使用済みか、無効です。',
  },
  name_required: {
    ko: '이름은 필수입니다', en: 'Name is required.', ja: '名前は必須です。',
  },
  authentication_required: {
    ko: '다시 로그인해 주세요.',
    en: 'Please sign in again.',
    ja: 'もう一度ログインしてください。',
  },
  forbidden: {
    ko: '이 작업을 수행할 권한이 없습니다.',
    en: 'You do not have permission to perform this action.',
    ja: 'この操作を行う権限がありません。',
  },
  admin_login_required: {
    ko: '관리자 세션으로 다시 로그인해 주세요.',
    en: 'Please sign in again with your admin account.',
    ja: '管理者アカウントでもう一度ログインしてください。',
  },
  admin_restore_failed: {
    ko: '관리자 세션을 복구하지 못했습니다.',
    en: 'The admin session could not be restored.',
    ja: '管理者セッションを復元できませんでした。',
  },
  insufficient_credits: {
    ko: '크레딧이 부족합니다', en: 'You do not have enough credits.', ja: 'クレジットが不足しています。',
  },
  unsupported_format: {
    ko: '지원하지 않는 파일 형식입니다.',
    en: 'This file format is not supported.',
    ja: 'このファイル形式には対応していません。',
  },
  score_file_required: {
    ko: '악보 이미지 또는 PDF 파일만 업로드할 수 있습니다',
    en: 'Upload a score image or PDF file.',
    ja: '楽譜の画像またはPDFファイルをアップロードしてください。',
  },
  imported_score_incomplete: {
    ko: '업로드한 악보가 곡 전체와 동기화될 만큼 충분히 인식되지 않았습니다. PDF나 이미지의 모든 페이지가 선명하게 보이도록 다시 업로드해 주세요.',
    en: 'The score was not recognized fully enough to follow the whole song. Upload all PDF or image pages again, making sure they are clear.',
    ja: '曲全体に同期できるだけの譜面を認識できませんでした。PDFや画像の全ページが鮮明に見えるようにして、もう一度アップロードしてください。',
  },
  empty_file: {
    ko: '빈 파일은 업로드할 수 없습니다',
    en: 'The file is empty. Choose another file.',
    ja: 'ファイルが空です。別のファイルを選んでください。',
  },
  file_too_large: {
    ko: '파일이 너무 큽니다. 더 작은 파일을 선택해 주세요.',
    en: 'The file is too large. Choose a smaller file.',
    ja: 'ファイルが大きすぎます。小さいファイルを選んでください。',
  },
  tab_stem_unsupported: {
    ko: '이 트랙은 악보 생성을 지원하지 않습니다.',
    en: 'Score generation is not available for this track.',
    ja: 'このトラックは譜面生成に対応していません。',
  },
  separation_incomplete: {
    ko: '분리가 완료된 후 시도하세요',
    en: 'Try again after separation is complete.',
    ja: '分離が完了してから、もう一度お試しください。',
  },
  source_download_failed: {
    ko: '유튜브 오디오 다운로드에 실패했습니다.',
    en: 'The YouTube audio could not be downloaded. Check the link and try again.',
    ja: 'YouTubeの音声をダウンロードできませんでした。リンクを確認して、もう一度お試しください。',
  },
  // 분리 행의 failure_code 로만 매칭한다. 같은 원문을 쓰는 악보(탭) 실패에는
  // "다시 올려 주세요"가 맞지 않으므로 LEGACY_CODES 에 원문을 넣지 않는다.
  server_restart_cleanup: {
    ko: '서버 점검으로 분리가 중단됐습니다. 크레딧은 차감되지 않았습니다. 곡을 다시 올려 주세요.',
    en: 'Server maintenance interrupted this separation. No credits were used. Please upload the song again.',
    ja: 'サーバーメンテナンスのため分離が中断されました。クレジットは消費されていません。曲をもう一度アップロードしてください。',
  },
  cancelled: {
    ko: '트랙 분리 작업이 취소되었습니다.',
    en: 'Track separation was cancelled.',
    ja: 'トラック分離がキャンセルされました。',
  },
  too_many_requests: {
    ko: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
    en: 'There are too many requests. Please try again shortly.',
    ja: 'リクエストが多すぎます。少し待ってから、もう一度お試しください。',
  },
  youtube_duration: {
    ko: '15분 이하 유튜브 링크만 지원합니다.',
    en: 'Use a YouTube video that is 15 minutes or shorter.',
    ja: '15分以下のYouTube動画を使用してください。',
  },
  youtube_live: {
    ko: '실시간 방송 링크는 아직 지원하지 않습니다.',
    en: 'Live streams are not supported yet.',
    ja: 'ライブ配信にはまだ対応していません。',
  },
  video_score_single_video: {
    ko: '영상 하나의 유튜브 링크를 입력해 주세요.',
    en: 'Enter a YouTube link to a single video.',
    ja: 'YouTubeの動画1本のリンクを入力してください。',
  },
  video_score_size: {
    ko: '256MB 이하의 영상만 지원합니다.',
    en: 'Use a video no larger than 256MB.',
    ja: '256MB以下の動画を使用してください。',
  },
  video_score_save_size: {
    ko: '영상을 저장하지 못했습니다. 256MB 이하의 영상만 지원합니다.',
    en: 'The video could not be saved. Use a video no larger than 256MB.',
    ja: '動画を保存できませんでした。256MB以下の動画を使用してください。',
  },
  video_score_duration: {
    ko: '길이를 확인할 수 있는 10분 이하의 영상을 입력해 주세요.',
    en: 'Use a video with a known duration of 10 minutes or less.',
    ja: '長さを確認できる10分以下の動画を使用してください。',
  },
  video_score_live_or_playlist: {
    ko: '실시간 방송이나 재생목록은 지원하지 않습니다.',
    en: 'Live streams and playlists are not supported.',
    ja: 'ライブ配信や再生リストには対応していません。',
  },
  video_score_private: {
    ko: '영상을 가져오지 못했습니다. 공개된 영상인지 확인해 주세요.',
    en: 'The video could not be retrieved. Check that it is public.',
    ja: '動画を取得できませんでした。公開されている動画か確認してください。',
  },
  video_score_download: {
    ko: '영상을 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.',
    en: 'The video could not be retrieved. Please try again shortly.',
    ja: '動画を取得できませんでした。少し待ってから、もう一度お試しください。',
  },
  video_score_download_interrupted: {
    ko: '영상을 가져오는 중 문제가 생겼습니다. 잠시 후 다시 시도해 주세요.',
    en: 'There was a problem retrieving the video. Please try again shortly.',
    ja: '動画の取得中に問題が発生しました。少し待ってから、もう一度お試しください。',
  },
  video_score_unclear: {
    ko: '이 영상의 악보를 정확하게 추출하기 어렵습니다. 더 선명한 영상을 사용해 주세요.',
    en: 'The score could not be extracted accurately. Please use a clearer video.',
    ja: '楽譜を正確に抽出できませんでした。より鮮明な動画を使用してください。',
  },
  video_score_interrupted: {
    ko: '악보 추출이 중단되었습니다. 다시 시도해 주세요.',
    en: 'Score extraction was interrupted. Please try again.',
    ja: '楽譜の抽出が中断されました。もう一度お試しください。',
  },
  video_score_failed: {
    ko: '악보를 추출하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    en: 'The score could not be extracted. Please try again shortly.',
    ja: '楽譜を抽出できませんでした。少し待ってから、もう一度お試しください。',
  },
  video_score_not_found: {
    ko: '영상에서 추출할 수 있는 악보를 찾지 못했습니다.',
    en: 'No extractable score was found in the video.',
    ja: '動画内に抽出できる楽譜が見つかりませんでした。',
  },
} satisfies Record<string, LocalizedValue<string>>;

type ErrorCode = keyof typeof ERROR_COPY;

const LEGACY_CODES: Record<string, ErrorCode> = {
  '이메일 또는 비밀번호가 올바르지 않습니다': 'invalid_credentials',
  '이미 사용 중인 이메일입니다': 'email_already_used',
  '비밀번호는 8자 이상이어야 합니다': 'password_too_short',
  '초대 코드가 이미 사용되었거나 유효하지 않습니다': 'invalid_invite',
  '이름은 필수입니다': 'name_required',
  '인증이 필요합니다': 'authentication_required',
  '로그인이 필요합니다': 'authentication_required',
  '관리자 권한이 필요합니다': 'forbidden',
  '관리자 세션으로 다시 로그인해 주세요': 'admin_login_required',
  '관리자 세션을 복구하지 못했습니다': 'admin_restore_failed',
  '크레딧이 부족합니다': 'insufficient_credits',
  '분리 크레딧이 부족합니다': 'insufficient_credits',
  '악보 이미지 또는 PDF 파일만 업로드할 수 있습니다': 'score_file_required',
  '빈 파일은 업로드할 수 없습니다': 'empty_file',
  '빈 오디오 파일은 업로드할 수 없습니다': 'empty_file',
  '분리가 아직 완료되지 않았습니다': 'separation_incomplete',
  '분리가 완료된 후 시도하세요': 'separation_incomplete',
  '트랙 분리가 완료되지 않았습니다': 'separation_incomplete',
  '유튜브 오디오 다운로드에 실패했습니다': 'source_download_failed',
  '트랙 분리 작업이 취소되었습니다': 'cancelled',
  '15분 이하 유튜브 링크만 지원합니다': 'youtube_duration',
  '실시간 방송 링크는 아직 지원하지 않습니다': 'youtube_live',
  // Public messages from api/video_score.py and VideoScoreMediaError only.
  '영상 하나의 유튜브 링크를 입력해 주세요': 'video_score_single_video',
  '256MB 이하의 영상만 지원합니다': 'video_score_size',
  '영상을 저장하지 못했습니다. 256MB 이하의 영상만 지원합니다': 'video_score_save_size',
  '길이를 확인할 수 있는 10분 이하의 영상을 입력해 주세요': 'video_score_duration',
  '실시간 방송이나 재생목록은 지원하지 않습니다': 'video_score_live_or_playlist',
  '영상을 가져오지 못했습니다. 공개된 영상인지 확인해 주세요': 'video_score_private',
  '영상을 가져오지 못했습니다. 잠시 후 다시 시도해 주세요': 'video_score_download',
  '영상을 가져오는 중 문제가 생겼습니다. 잠시 후 다시 시도해 주세요': 'video_score_download_interrupted',
  '이 영상의 악보를 정확하게 추출하기 어렵습니다. 더 선명한 영상을 사용해 주세요': 'video_score_unclear',
  '악보 추출이 중단되었습니다. 다시 시도해 주세요': 'video_score_interrupted',
  '악보를 추출하지 못했습니다. 잠시 후 다시 시도해 주세요': 'video_score_failed',
  '영상에서 추출할 수 있는 악보를 찾지 못했습니다': 'video_score_not_found',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function knownCode(value: unknown): ErrorCode | null {
  return typeof value === 'string' && Object.hasOwn(ERROR_COPY, value) ? value as ErrorCode : null;
}

/**
 * 같은 요청을 다시 보내도 결과가 같은 클라이언트 오류인가.
 * 이런 오류의 fallback 문구에는 "잠시 후 다시 시도"를 붙이지 않는다.
 * 408/409/425/429 는 기다리거나 상태가 바뀌면 통과하므로 제외한다.
 */
export function isNonRetryableRequestError(error: unknown): boolean {
  const status = asRecord(error)?.status;
  return typeof status === 'number'
    && status >= 400
    && status < 500
    && ![408, 409, 425, 429].includes(status);
}

/** Translate only recognized public errors. Unknown backend/technical text stays out of the UI. */
export function localizeRequestError(
  language: SupportedLanguage,
  error: unknown,
  fallback: string,
): string {
  const record = asRecord(error);
  const body = asRecord(record?.body);
  const detail = body?.detail;
  const detailRecord = asRecord(detail);
  const code = knownCode(detailRecord?.code)
    ?? knownCode(body?.code)
    ?? knownCode(record?.code)
    ?? knownCode(record?.failure_code);
  if (code) return pickLocalized(language, ERROR_COPY[code]);

  const raw = typeof detail === 'string' ? detail
    : typeof detailRecord?.message === 'string' ? detailRecord.message
      : typeof error === 'string' ? error
        : typeof record?.message === 'string' ? record.message : '';
  const message = raw.trim().replace(/[.。]$/, '');
  const legacyCode = Object.hasOwn(LEGACY_CODES, message) ? LEGACY_CODES[message] : undefined;
  if (legacyCode) return pickLocalized(language, ERROR_COPY[legacyCode]);

  // Persisted OMR failures predate error codes. Recognize only complete public
  // coverage messages; replace numeric diagnostics with translated recovery advice.
  const incompleteScore = /^업로드한 악보가 곡 전체와 동기화될 만큼 충분히 인식되지 않았습니다(?: \(예상 \d+(?:\.\d+)?초 대비 \d+(?:\.\d+)?초 분량\))?\. PDF\/이미지 전체 페이지가 선명하게 보이도록 다시 업로드해 주세요$/;
  const partialPdf = /^업로드한 PDF는 \d+페이지인데 MusicXML이 \d+마디만 인식되었습니다\. 첫 일부만 변환된 것으로 보여 PDF\/이미지 전체 페이지가 선명하게 보이도록 다시 업로드해 주세요$/;
  if (incompleteScore.test(message) || partialPdf.test(message)) {
    return pickLocalized(language, ERROR_COPY.imported_score_incomplete);
  }

  // Preserve only validated numeric limits; never interpolate filenames, URLs, or raw server text.
  const size = message.match(/^(?:업로드|녹음) 파일이 너무 큽니다 \(최대 (\d{1,6})MB\)$/)?.[1];
  if (size) return pickLocalized(language, {
    ko: `파일이 너무 큽니다. ${size}MB 이하의 파일을 선택해 주세요.`,
    en: `The file is too large. Choose a file no larger than ${size}MB.`,
    ja: `ファイルが大きすぎます。${size}MB以下のファイルを選んでください。`,
  });
  if (/^지원하지 않는 형식: \.[a-z0-9]{1,10}$/i.test(message)) {
    return pickLocalized(language, ERROR_COPY.unsupported_format);
  }
  if (/^허용되지 않는 스템: [a-z0-9_]{1,40}$/.test(message)) {
    return pickLocalized(language, ERROR_COPY.tab_stem_unsupported);
  }

  const statusCodes: Partial<Record<number, ErrorCode>> = {
    401: 'authentication_required', 402: 'insufficient_credits', 403: 'forbidden',
    413: 'file_too_large', 429: 'too_many_requests',
  };
  const statusCode = typeof record?.status === 'number' ? statusCodes[record.status] : undefined;
  return statusCode ? pickLocalized(language, ERROR_COPY[statusCode]) : fallback;
}
